/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { ApolloClient, InMemoryCache, split, from, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';

import { envIs } from '@fai/friendli-suite/shared/environments/conditionals';
import { environment } from '@fai/friendli-suite/shared/environments/environment';

import errorHandler from './error-handler';
import { typePolicies } from './type-policies';

const endpointURI = `${environment.SERVER_URL}${environment.GRAPHQL_PATH}`;

const httpLink = createUploadLink({
  uri: endpointURI,
});
const wsLink = new GraphQLWsLink(
  createClient({ url: `${environment.WS_URL}${environment.GRAPHQL_PATH}` })
);

const namedLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${endpointURI}?${operation.operationName}${
      envIs.PRODUCTION
        ? ''
        : `${
            Object.keys(operation.variables).length > 0
              ? `?${JSON.stringify(operation.variables).replace(/"/g, '')}`
              : ''
          }`
    }`,
  }));

  return forward ? forward(operation) : null;
});

const link = split(
  ({ query }) => {
    const def = getMainDefinition(query);

    return def.kind === 'OperationDefinition' && def.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const cache = new InMemoryCache({ typePolicies });

const client = new ApolloClient({
  link: from([onError(errorHandler), namedLink, link]),
  cache,
  connectToDevTools: environment.ENV_NAME !== 'PRODUCTION',
  // @Lee-Si-Yoon: leave this for test configurations
  // defaultOptions: { watchQuery: { fetchPolicy: 'no-cache' } },
});

export default client;
